import React from 'react';
import styles from './ActionIcons.module.css';

export type ActionIconProps = {
  className?: string;
  size?: number;
};

const svgProps = (className: string | undefined, size: number) => ({
  className,
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  'aria-hidden': true,
  focusable: false as const,
});

export const SettingsIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const CreateIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="8.25" />
    <path d="M12 8.5v7M8.5 12h7" />
  </svg>
);

export const SaveIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 5.2h10.1L18.8 8.4V18.8A1.2 1.2 0 0 1 17.6 20H6.4A1.2 1.2 0 0 1 5.2 18.8V6.4A1.2 1.2 0 0 1 6.4 5.2Z" />
    <path d="M8.2 5.2v4.2h7.2V5.2" />
    <path d="M8.4 20v-5.4h7.2V20" />
  </svg>
);

export const CategoryIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.6 7.2V6.4A1.6 1.6 0 0 1 6.2 4.8h4.1L12 6.6h5.8A1.6 1.6 0 0 1 19.4 8.2v9.4A1.6 1.6 0 0 1 17.8 19.2H6.2A1.6 1.6 0 0 1 4.6 17.6V7.2Z" />
  </svg>
);

export const StatusWorkflowIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4.5" y="4.8" width="4.4" height="14.4" rx="1.2" />
    <rect x="9.8" y="4.8" width="4.4" height="9.4" rx="1.2" />
    <rect x="15.1" y="4.8" width="4.4" height="6.6" rx="1.2" />
  </svg>
);

export const CommentsIcon: React.FC<ActionIconProps> = ({ className = styles.tabIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.4 6.2h13.2A1.4 1.4 0 0 1 20 7.6v7.2a1.4 1.4 0 0 1-1.4 1.4H10.2L6 19.4v-3.2H5.4A1.4 1.4 0 0 1 4 14.8V7.6A1.4 1.4 0 0 1 5.4 6.2Z" />
  </svg>
);

export const ActivityIcon: React.FC<ActionIconProps> = ({ className = styles.tabIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 12.2h3.2l1.8-4.6 3.2 9 2.1-4.4H19.5" />
  </svg>
);

export const ChartIcon: React.FC<ActionIconProps> = ({ className = styles.buttonIcon, size = 16 }) => (
  <svg {...svgProps(className, size)} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.8 19.2h14.4" />
    <path d="M7 19.2V11.2" />
    <path d="M12 19.2V6.8" />
    <path d="M17 19.2v-5.2" />
  </svg>
);

export const ButtonWithIcon: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <>
    {icon}
    <span>{children}</span>
  </>
);

export { styles as actionIconStyles };
